import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { scrollToDemo } from '../utils/navigation';
import {
  useLandingNamespace,
  useLanguageLanding,
} from '../context/LanguageLandingContext';
import './Hero.css';
import posthog from 'posthog-js';
import { openAdobeExchange } from '../utils/analytics';
import { isSafariBrowser } from '../utils/safariMotion';

const Hero: React.FC = () => {
  const safari = isSafariBrowser();
  const { language } = useLanguageLanding();
  const ns = useLandingNamespace();
  const { t } = useTranslation(ns);

  const handleTryFree = () => {
    posthog.capture('hero_cta_clicked', {
      destination: 'adobe_exchange',
      language: language?.slug ?? null,
    });
    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void })
      .gtag;
    if (typeof window !== 'undefined' && gtag) {
      gtag('event', 'conversion_event_signup', {});
    }
    openAdobeExchange('hero', { language: language?.slug ?? null });
  };

  return (
    <section className="hero">
      <div className="hero-container">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <motion.div
            className="hero-adobe-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <img src="/AdobeLogo.png" alt="Adobe" className="hero-adobe-logo" />
            <span className="hero-adobe-text">{t('adobeBadge')}</span>
          </motion.div>
          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('heroBefore')}
            <span className="highlight">{t('heroHighlight')}</span>
            {t('heroAfter')}
          </motion.h1>
          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {t('heroSubtitle')}
          </motion.p>
          <motion.div
            className="hero-buttons"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <motion.button
              className="cta-button primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleTryFree}
            >
              {t('installCta')}
            </motion.button>
            <motion.button
              className="cta-button secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToDemo}
            >
              {t('watchDemoCta')}
            </motion.button>
          </motion.div>
        </motion.div>
        <motion.div
          className="hero-animation"
          initial={{ opacity: 0, x: 50, scale: 0.9 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
        >
          {safari ? (
            <video
              className="hero-video"
              autoPlay
              loop
              muted
              playsInline
              controls={false}
            >
              <source
                src={encodeURI('/Substyle Landing Page 2.mov')}
                type="video/quicktime"
              />
              Your browser does not support the video tag.
            </video>
          ) : (
            <video
              className="hero-video"
              autoPlay
              loop
              muted
              playsInline
              controls={false}
            >
              <source
                src="https://ittechllc.s3.us-east-2.amazonaws.com/Substyle/Substyle+Landing+Page.webm"
                type="video/webm"
              />
              Your browser does not support the video tag.
            </video>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
